/** @jsxImportSource @emotion/react */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { Avatar, Button, IconButton } from '@mui/material'
import ButtonGroup from '@mui/material/ButtonGroup'

import { useLazyQuery, useQuery } from '@apollo/client'
import { sortBy, uniqBy } from 'lodash'
import { ReactComponent as IconPlus } from 'assets/icons/plus.svg'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table, { FooterWrapper, PaginationPage, PaginationTake } from 'components/Table'
import { Search } from 'components/Input'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import {
  findAllUsers_findAllUsers_items,
  findAllUsers_findAllUsers_items_purchases as Purchases,
} from '__generatedTypes__/findAllUsers'
import Select from 'components/Select'
import { GET_PLATFORMS } from 'queries/common'
import { findAllPlatforms, findAllPlatformsVariables } from '__generatedTypes__/findAllPlatforms'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import Tooltip from 'components/Tooltip'
import {
  MembershipStatus,
  PlatformName,
  ProductTypeEnum,
  RolesEnum,
  SearchTypeEnum,
} from '__generatedTypes__/globalTypes'
import { GENERATE_CSV, GET_USERS_COUNT_BY_ROLES } from 'queries/users'
import { findUsersCountByRoles } from '__generatedTypes__/findUsersCountByRoles'
import { PageLoader } from 'components/PageLoader'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { GenerateCSV, GenerateCSVVariables } from '__generatedTypes__/GenerateCSV'
import { saveBase64AsCSV } from 'utils/saveFile'
import { authService } from 'utils/services/auth'
import { useAuthContext } from 'utils/auth'
import DateTimeInput from 'components/DateTimeInput'
import {
  findAllProduct,
  findAllProductVariables,
  findAllProduct_findAllProduct_items as Product,
} from '__generatedTypes__/findAllProduct'
import { FIND_ALL_PRODUCT } from 'queries/products'
import { useUsers } from './useUsers'
import * as classes from './styles'
import { TooltipTitle, PurchaseKey } from './components/TooltipTitle'
import { getFilterParams } from './utils'

export const MEMBERSHIP_TYPES = [
  {
    type: 'active' as PurchaseKey,
    title: 'Active',
    key: 'activeCount' as keyof Purchases,
    css: classes.activeMembership,
  },
  {
    type: 'paused' as PurchaseKey,
    title: 'Paused',
    key: 'pausedCount' as keyof Purchases,
    css: classes.pausedMembership,
  },
  {
    type: 'expired' as PurchaseKey,
    title: 'Expired',
    key: 'expiredCount' as keyof Purchases,
    css: classes.expiredMembership,
  },
]

const STATUS_OPTIONS = [
  {
    id: MembershipStatus.Active,
    title: 'Active',
  },
  {
    id: MembershipStatus.Paused,
    title: 'Paused',
  },
  {
    id: MembershipStatus.Expired,
    title: 'Expired',
  },
]

export const ROLE_TITLE = {
  [RolesEnum.User]: 'Student',
  [RolesEnum.Admin]: 'Administrator',
  [RolesEnum.SuperAdmin]: 'Super Administrator',
  [RolesEnum.Teacher]: 'Faculty Member',
  [RolesEnum.AITrainer]: 'AI Trainer',
  [RolesEnum.Marketing]: 'Marketing',
  [RolesEnum.UGCCreator]: 'UGC Creator',
}

export const List = () => {
  const { getLink } = useLink()

  const { data: rolesCount } = useQuery<findUsersCountByRoles>(GET_USERS_COUNT_BY_ROLES, {
    fetchPolicy: 'network-only',
  })

  const [generateUsersCSV, { loading: usersCSVLoading }] = useLazyQuery<GenerateCSV, GenerateCSVVariables>(
    GENERATE_CSV,
    {
      fetchPolicy: 'network-only',
    }
  )

  const { data: platformsData, loading: platformsLoading } = useQuery<findAllPlatforms, findAllPlatformsVariables>(
    GET_PLATFORMS,
    {
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
        },
      },
    }
  )

  const { data: productsData, loading: productsLoading } = useQuery<findAllProduct, findAllProductVariables>(
    FIND_ALL_PRODUCT,
    {
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
        },
      },
    }
  )

  const { isSuperAdmin } = useAuthContext()
  const usersState = useUsers()
  const { items, count, loading, refetch, pagination, search, filters, order, onFilterChange } = usersState
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    ;(async () => {
      const currentUserId = await authService.getUserId()
      setCurrentUserId(currentUserId)
    })()
  }, [refetch])

  const platformItems = platformsData?.findAllPlatforms.items || []

  const platforms = useMemo(
    () =>
      platformItems.map((platform) => ({
        id: platform.title,
        title: platform.title,
      })) || [],
    [platformItems]
  )

  const membershipLevels = useMemo(() => {
    const products = productsData?.findAllProduct?.items || []
    if (!products?.length) {
      return []
    }

    const selectedPlatformId = filters.selectedPlatform?.id

    const getMembershipLevelOption = (product: Product) => ({
      id: product.duration,
      title: `${product.duration}-Day`,
    })

    const premiumSortedProducts = sortBy(
      products.filter((product) => [ProductTypeEnum.Premium, ProductTypeEnum.PremiumMax].includes(product.type)),
      (product) => Number(product.duration)
    )

    if (!selectedPlatformId) {
      return uniqBy(premiumSortedProducts, 'duration').map(getMembershipLevelOption)
    }

    const platformProducts = premiumSortedProducts.filter((product) => product.platform.title === selectedPlatformId)

    return platformProducts.map(getMembershipLevelOption)
  }, [productsData, filters.selectedPlatform?.id])

  const handleExportClick = async () => {
    try {
      const where = getFilterParams(filters)

      const { data } = await generateUsersCSV({
        variables: {
          query: {
            where,
            search: { query: search.value, type: SearchTypeEnum.ILike },
          },
        },
      })
      if (!data?.generateCSV) return

      saveBase64AsCSV(data.generateCSV, 'users.csv')
    } catch (error) {
      console.error('An error occurred while downloading users data', error)
    }
  }

  if (!usersState.loaded || !currentUserId) {
    return <PageLoader />
  }

  const {
    all,
    admins: adminsCount,
    superAdmins: superAdminsCount,
    students: studentsCount,
    marketing: marketingCount,
    teacher: facultyCount,
    aiTrainers: aiTrainersCount,
  } = rolesCount?.findUsersCountByRoles || {}

  const roleFilters = [
    {
      title: 'All',
      count: all,
      role: undefined,
    },
    {
      title: 'Super admin',
      count: superAdminsCount,
      role: RolesEnum.SuperAdmin,
    },
    {
      title: 'Administrator',
      count: adminsCount,
      role: RolesEnum.Admin,
    },
    {
      title: 'Faculty Member',
      count: facultyCount,
      role: RolesEnum.Teacher,
    },
    {
      title: 'AI Trainer',
      count: aiTrainersCount,
      role: RolesEnum.AITrainer,
    },
    {
      title: 'Marketing',
      count: marketingCount,
      role: RolesEnum.Marketing,
    },
    {
      title: 'Students',
      count: studentsCount,
      role: RolesEnum.User,
    },
  ]

  const handlePlatformFilterChange = (
    value: {
      id: string
    } | null
  ) => {
    const selectedDuration = filters.duration?.id

    const shouldResetDuration =
      productsData?.findAllProduct.items.filter(
        (product) => product.platform.title === value?.id && product.duration === selectedDuration
      ).length === 0

    if (shouldResetDuration) {
      onFilterChange('duration', undefined)
    }

    onFilterChange('selectedPlatform', value ?? undefined)
  }

  return (
    <section css={classes.container}>
      <div css={classes.header}>
        <Typography css={classes.title} variant="title3">
          Users
        </Typography>

        {isSuperAdmin && (
          <Button
            css={classes.exportButton}
            color="blue"
            disabled={usersCSVLoading}
            startIcon={<IconPlus />}
            onClick={handleExportClick}
          >
            Export Data
          </Button>
        )}
      </div>
      <div>
        <ButtonGroup css={classes.buttonGroup}>
          {roleFilters.map(({ title, count, role }) => (
            <Button
              key={title}
              variant="text"
              css={classes.roleFilterButton(filters.role === role)}
              onClick={() => onFilterChange('role', role)}
            >
              {title} ({count || 0})
            </Button>
          ))}
        </ButtonGroup>
      </div>
      <div css={classes.toolbarWrapper}>
        <div css={classes.filterInputs}>
          <Select
            css={classes.filter}
            items={platforms}
            value={filters.selectedPlatform || null}
            onChange={handlePlatformFilterChange}
            renderItem={(platform) => getFullPlatformTitle(platform.title as PlatformName)}
            getItemLabel={(platform) => getFullPlatformTitle(platform.title as PlatformName) ?? platform.title}
            placeholder="Active Platform"
            loading={platformsLoading}
            displaySelectedItemsFirst
          />

          <Select
            css={classes.filter}
            items={STATUS_OPTIONS}
            value={filters.status || null}
            onChange={(value) => onFilterChange('status', value ?? undefined)}
            placeholder="Status"
            loading={platformsLoading}
            displaySelectedItemsFirst
          />

          <Select
            css={classes.filter}
            items={membershipLevels}
            value={filters.duration || null}
            onChange={(value) => onFilterChange('duration', value ?? undefined)}
            placeholder="Membership level"
            loading={platformsLoading || productsLoading}
            displaySelectedItemsFirst
          />

          <DateTimeInput
            css={classes.filter}
            value={filters.expirationDate?.from ?? null}
            onChange={(value) => {
              onFilterChange('expirationDate', {
                ...filters.expirationDate,
                from: value || undefined,
              })
            }}
            placeholder="Exp. date from"
            pickerType="date"
            format="MMMM d, yyyy"
            shouldDisableDate={(date) => !!filters.expirationDate?.to && date > filters.expirationDate.to}
            withConfirmation={false}
            withClearIcon
          />

          <DateTimeInput
            css={classes.filter}
            value={filters.expirationDate?.to ?? null}
            onChange={(value) => {
              onFilterChange('expirationDate', {
                ...filters.expirationDate,
                to: value || undefined,
              })
            }}
            placeholder="Exp. date to"
            pickerType="date"
            format="MMMM d, yyyy"
            withConfirmation={false}
            shouldDisableDate={(date) => !!filters.expirationDate?.from && date < filters.expirationDate.from}
            withClearIcon
          />

          <Search {...search} />
        </div>
      </div>
      <Table
        css={classes.tableWrapper}
        columns={[
          {
            title: 'Full Name',
            renderCell: (user) => (
              <div css={classes.titleWrapper}>
                <div css={classes.fullNameCell}>
                  <Avatar src={user?.avatar ?? undefined} alt="Avatar" />
                  {user.firstName} {user.lastName}
                </div>
              </div>
            ),
            flex: 2,
          },
          {
            title: 'Email',
            renderCell: (user) => <div css={classes.titleWrapper}>{user.email}</div>,
            flex: 2,
          },
          {
            title: 'Role',
            renderCell: (user) => <div css={classes.titleWrapper}>{ROLE_TITLE[user.role]}</div>,
            flex: 1,
          },
          {
            title: 'Purchases',
            renderCell: (user) => (
              <div css={[classes.titleWrapper, classes.membershipsCell]}>
                {user.role === RolesEnum.SuperAdmin
                  ? '-'
                  : MEMBERSHIP_TYPES.map(({ type, title, key, css }) => {
                      const purchases = user.purchases[key] || 0

                      return (
                        <Tooltip
                          key={key}
                          title={<TooltipTitle purchases={user.purchases} type={type} title={`${title} Memberships`} />}
                          arrow
                          placement="right"
                          {...(purchases === 0 ? { open: false } : {})}
                        >
                          <span css={css}>
                            {title}({purchases})
                          </span>
                        </Tooltip>
                      )
                    })}
              </div>
            ),
            flex: 1,
          },
          {
            title: 'Membership Lv.',
            renderCell: (user) => (
              <div css={[classes.titleWrapper, classes.membershipsCell]}>
                {user.role === RolesEnum.SuperAdmin
                  ? '-'
                  : user.membershipLvl.map((level) => <span key={level}>{level}</span>)}
              </div>
            ),
            flex: 1,
          },
          {
            title: 'Expiration',
            renderCell: (user) => (
              <div css={[classes.titleWrapper, classes.membershipsCell]}>
                {user.role === RolesEnum.SuperAdmin
                  ? '-'
                  : user.expiration.map((level) => <span key={level}>{level}</span>)}
              </div>
            ),
            orderField: filters.selectedPlatform ? 'expirationDate' : undefined,
            flex: '0 1 96px',
          },
          {
            title: '',
            renderCell: (user) =>
              user.role === RolesEnum.SuperAdmin && !isSuperAdmin ? (
                ''
              ) : (
                <Link to={getLink(cmsRoutes.USER_EDIT, { userId: user.id })}>
                  <IconButton size="small" css={classes.action} disabled={loading}>
                    <EditIcon />
                  </IconButton>
                </Link>
              ),
            flex: '0 1 64px',
          },
        ]}
        order={order.value}
        onOrder={order.onChange}
        items={items as findAllUsers_findAllUsers_items[]}
        wrapperMinWidth={850}
      />
      <FooterWrapper css={classes.footer}>
        <PaginationTake {...pagination} />
        <PaginationPage totalCount={count ?? 0} {...pagination} />
      </FooterWrapper>
    </section>
  )
}
